<template>
  <!-- The byline: who wrote it, when, and how long it takes to read.
       Every date is a <time datetime="YYYY-MM-DD"> so a crawler can read the
       article's age from the markup rather than parsing the prose. -->
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
    <span v-if="showAuthor && post.author" class="text-gray-400">{{ post.author }}</span>
    <span v-if="showAuthor && post.author" aria-hidden="true">·</span>

    <time :datetime="toDateAttr(post.date)">{{ published }}</time>

    <!-- Only shown once a post has actually been revised. "Updated" on a piece
         that has never changed is noise, and it is the signal a reader uses to
         decide whether an old-looking date still matters. -->
    <template v-if="updated">
      <span aria-hidden="true">·</span>
      <span>Updated <time :datetime="toDateAttr(post.updated)">{{ updated }}</time></span>
    </template>

    <span aria-hidden="true">·</span>
    <span>{{ post.readingMinutes }} min read</span>

    <!-- Drafts only ever reach a page in `nuxt dev` (server/utils/blog.ts drops
         them everywhere else), so this badge is a working aid: it makes it
         obvious which of the posts on screen would not exist in production. -->
    <BaseBadge v-if="post.status === 'draft'" tone="warning" class="uppercase tracking-wide">Draft</BaseBadge>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BaseBadge from '~/components/atoms/BaseBadge.vue'
import { formatPostDate, formatPostDateShort, toDateAttr } from '~/utils/postDate'

const props = defineProps({
  // A PostSummary from server/utils/blog.ts.
  post: { type: Object, required: true },
  showAuthor: { type: Boolean, default: true },
  // The listing runs several of these side by side, where the long form wraps.
  compact: { type: Boolean, default: false },
})

const format = computed(() => (props.compact ? formatPostDateShort : formatPostDate))
const published = computed(() => format.value(props.post.date))
const updated = computed(() => (props.post.updated ? format.value(props.post.updated) : ''))
</script>
